import {
  Box,
  Checkbox,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ChecklistIcon from "@mui/icons-material/Checklist";
import { LeadsManagementData } from "../hooks/useLeadsManagementData";
import StatusChip from "../StatusChip";
import {
  STATUS_META,
  formatCurrency,
  isRejectableStatus,
  getScoreMeta,
  getQualifiedMeta,
} from "../constants";

interface LeadsTableProps {
  data: LeadsManagementData;
}

type SortableField =
  | "id"
  | "name"
  | "brand"
  | "status"
  | "date"
  | "leadScore"
  | "approvalLikelihood"
  | "qualifiedByBrand"
  | "incomeFromBrand";

type ColumnDef =
  | { type: "sort"; field: SortableField; label: string; width: number; align?: "right" }
  | { type: "plain"; label: string; width: number; align?: "right" };

// Header layout only — body cells below are hardcoded JSX kept in the same
// order, same convention as before this column set grew.
// IP Address stays "plain": has_duplicate_ip is a shared-vs-unique boolean
// on the server, not a per-lead value worth alphabetizing, and the cell
// itself renders more than one IP-derived thing (the address plus the
// duplicate-IP badge) — there's no single sort key that would represent it.
// Widths are the fixed column widths from the Figma table (table-layout:
// fixed, see TABLE_MIN_WIDTH) — ID's width includes its row checkbox.
const COLUMNS: ColumnDef[] = [
  { type: "sort", field: "id", label: "ID", width: 96 },
  { type: "sort", field: "name", label: "Full Name", width: 240 },
  { type: "sort", field: "leadScore", label: "Lead Score", width: 114 },
  { type: "sort", field: "approvalLikelihood", label: "Approval Likelihood", width: 168 },
  { type: "sort", field: "brand", label: "Brand Name", width: 172 },
  { type: "sort", field: "status", label: "Report Status", width: 144 },
  { type: "sort", field: "qualifiedByBrand", label: "Qualified By Brands", width: 168 },
  { type: "sort", field: "incomeFromBrand", label: "Income", width: 120 },
  { type: "sort", field: "date", label: "Acquired", width: 100 },
  { type: "plain", label: "IP Address", width: 114 },
  { type: "plain", label: "Brand Matches", width: 176 },
];

const TABLE_MIN_WIDTH = 1580;

// Checkbox + ID share the first column (10px apart) per the design; the
// checkbox's own 9px padding is dropped so that gap is the real visual gap.
// Kept inline (not a flex row) so the cell's baseline stays the ID text's
// baseline — a flex wrapper took the checkbox's bottom edge as its baseline,
// which lifted "ID"/the id above the other columns under verticalAlign:
// baseline. text-bottom lines the box up with the text instead.
const CHECKBOX_SX = {
  p: 0,
  mr: 1.25,
  verticalAlign: "text-bottom",
  "&.Mui-checked": { color: "secondary.main" },
};

const BODY2_SX = {
  fontFamily: "Roboto",
  fontWeight: 400,
  fontSize: 14,
  lineHeight: "143%",
  letterSpacing: "0.17px",
  color: "text.primary",
};

const CAPTION_SX = {
  fontFamily: "Roboto",
  fontWeight: 400,
  fontSize: 12,
  lineHeight: "166%",
  letterSpacing: "0.4px",
};

const SCORE_PALETTE: Record<string, "success" | "warning" | "error"> = {
  Strong: "success",
  Moderate: "warning",
  Weak: "error",
};

function ScoreCell({ score }: { score: number | null }) {
  const theme = useTheme();
  if (score === null) {
    return <Typography sx={{ fontSize: 13, color: "grey.400" }}>—</Typography>;
  }
  // getScoreMeta's thresholds, but the table's colours come from the theme
  // palette (success/warning/error.main) per the design — getScoreMeta's own
  // hex values stay as they are for LeadDetailDrawer.
  const { band } = getScoreMeta(score);
  const color = theme.palette[SCORE_PALETTE[band]].main;
  const tint = alpha(color, 0.08);
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <Box sx={{ width: 53, height: 4, borderRadius: 1, bgcolor: tint, overflow: "hidden" }}>
        <Box sx={{ width: `${score * 10}%`, height: "100%", borderRadius: "4px 0 0 4px", bgcolor: color }} />
      </Box>
      <Typography
        variant="caption"
        sx={{
          ...CAPTION_SX,
          color,
          bgcolor: tint,
          borderRadius: 1,
          px: 0.5,
        }}
      >
        {score.toFixed(1)}
      </Typography>
    </Box>
  );
}

// Acquired column renders date and time as two separate lines/styles —
// formatLeadDateTime (constants.ts) returns one combined "Mon D, YYYY, h:mm
// a.m." string for other places in this module (e.g. LeadDetailDrawer), so
// these stay local to this table rather than changing that shared helper's
// output for every other consumer.
//
// Uses UTC getters, not local ones — the start/end date filters sent to the
// API (date_from/date_to) are plain "YYYY-MM-DD" calendar days with no
// timezone, matched against the API in UTC. Reading this same timestamp
// back with local getters shifts it by the browser's UTC offset, which for
// anyone east of UTC can push a late-UTC-day lead into the next local day
// (e.g. showing up as the 4th here despite the filter being 1st–3rd, and
// the server having correctly matched it as the 3rd). UTC getters keep the
// displayed calendar day consistent with what was actually filtered.
export function formatAcquiredDate(iso: string): string {
  const date = new Date(iso);
  const day = String(date.getUTCDate()).padStart(2, "0");
  const month = String(date.getUTCMonth() + 1).padStart(2, "0");
  return `${day}/${month}/${date.getUTCFullYear()}`;
}

export function formatAcquiredTime(iso: string): string {
  const date = new Date(iso);
  const meridiem = date.getUTCHours() < 12 ? "am" : "pm";
  const hours = date.getUTCHours() % 12 || 12;
  const minutes = String(date.getUTCMinutes()).padStart(2, "0");
  return `${String(hours).padStart(2, "0")}:${minutes} ${meridiem}`;
}

export default function LeadsTable({ data }: LeadsTableProps) {
  const {
    pageLeads,
    totalFiltered,
    pageStartIndex,
    perPage,
    setPerPage,
    pageSizeOptions,
    page,
    totalPages,
    allChecked,
    selected,
    sortField,
    sortDirection,
    toggleSort,
    toggleAllSelectable,
    toggleLeadSelected,
    openLeadDetail,
    goToPage,
    goToFilteredLeads,
    openBrandMatch,
  } = data;

  return (
    <Paper elevation={0} sx={{ mx: -2, overflow: "hidden", border: "none" }}>
      <TableContainer sx={{ maxWidth: "100%", overflowX: "auto" }}>
        <Table
          size="small"
          sx={{
            minWidth: TABLE_MIN_WIDTH,
            tableLayout: "fixed",
            "& .MuiTableCell-root": { px: 1, verticalAlign: "baseline" },
            "& .MuiTableCell-root:first-of-type, & .MuiTableCell-root:nth-of-type(2)": { pl: 2 },
            "& .MuiTableCell-root:last-of-type": { pr: 2 },
            "& .MuiTableBody-root .MuiTableCell-root": { py: 1 },
          }}
        >
          <TableHead>
            <TableRow>
              {COLUMNS.map((column, index) => {
                const label =
                  column.type === "sort" ? (
                    <TableSortLabel
                      active={sortField === column.field}
                      direction={sortField === column.field ? sortDirection : "asc"}
                      onClick={() => toggleSort(column.field)}
                      sx={{
                        whiteSpace: "nowrap",
                        // Design keeps the sort arrow visible at rest, not
                        // only on hover/active.
                        "&:not(.Mui-active) .MuiTableSortLabel-icon": { opacity: 0.6, color: "text.disabled" },
                      }}
                    >
                      {column.label}
                    </TableSortLabel>
                  ) : (
                    column.label
                  );
                return (
                  <TableCell
                    key={column.label}
                    align={column.align}
                    sx={{ width: column.width, fontWeight: 500, letterSpacing: "0.17px" }}
                  >
                    {index === 0 ? (
                      <>
                        <Checkbox
                          size="small"
                          color="secondary"
                          checked={allChecked}
                          onChange={toggleAllSelectable}
                          sx={CHECKBOX_SX}
                        />
                        {label}
                      </>
                    ) : (
                      label
                    )}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {pageLeads.map((lead) => {
              const meta = STATUS_META[lead.status];
              const selectable = isRejectableStatus(lead.status);
              const qualifiedMeta = getQualifiedMeta(lead.qualifiedByBrand);
              const otherIpCount = lead.duplicateIpLeads.length;
              return (
                <TableRow key={lead.id} hover>
                  <TableCell sx={BODY2_SX}>
                    <Checkbox
                      size="small"
                      color="secondary"
                      checked={!!selected[lead.id]}
                      disabled={!selectable}
                      onChange={() => toggleLeadSelected(lead.id)}
                      data-tracker="928"
                      sx={CHECKBOX_SX}
                    />
                    {lead.id}
                  </TableCell>
                  <TableCell sx={{ width: 240, maxWidth: 240 }}>
                    <Typography
                      component="span"
                      onClick={() => openLeadDetail(lead.id)}
                      data-tracker="929"
                      sx={{
                        display: "inline-block",
                        fontFamily: "Roboto",
                        fontWeight: 500,
                        fontSize: 14,
                        lineHeight: "20px",
                        letterSpacing: "0.15px",
                        color: "secondary.main",
                        textDecoration: "underline",
                        textDecorationStyle: "solid",
                        cursor: "pointer",
                        "&:hover": { color: "secondary.dark" },
                      }}
                    >
                      {lead.name}
                    </Typography>
                    <Typography noWrap title={lead.email} sx={{ ...CAPTION_SX, color: "text.disabled", mt: 0.5, maxWidth: 240 }}>
                      {lead.email}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <ScoreCell score={lead.leadScore} />
                  </TableCell>
                  <TableCell>
                    <ScoreCell score={lead.approvalLikelihood} />
                  </TableCell>
                  <TableCell sx={BODY2_SX}>{lead.brand}</TableCell>
                  <TableCell>
                    <StatusChip label={meta.label} />
                  </TableCell>
                  <TableCell>
                    <StatusChip label={qualifiedMeta.label} />
                  </TableCell>
                  <TableCell
                    sx={
                      lead.incomeFromBrand
                        ? {
                            fontFamily: "Roboto",
                            fontWeight: 500,
                            fontSize: 14,
                            lineHeight: "157%",
                            letterSpacing: "0.1px",
                            color: "success.main",
                          }
                        : BODY2_SX
                    }
                  >
                    {formatCurrency(lead.incomeFromBrand)}
                  </TableCell>
                  <TableCell>
                    <Typography sx={BODY2_SX}>{formatAcquiredDate(lead.date)}</Typography>
                    <Typography sx={{ ...CAPTION_SX, color: "text.disabled", mt: 0.5 }}>
                      {formatAcquiredTime(lead.date)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography sx={BODY2_SX}>
                      {lead.ipAddress ?? "—"}
                    </Typography>
                    {otherIpCount > 0 && (() => {
                      const shownIds = lead.duplicateIpLeads.slice(0, 2).map((dup) => `#${dup.id}`).join(", ");
                      const remaining = otherIpCount - 2;
                      return (
                        <Box
                          onClick={() => lead.ipAddress && goToFilteredLeads({ search: lead.ipAddress })}
                          title={`${otherIpCount} other lead${otherIpCount === 1 ? "" : "s"} submitted from this IP`}
                          data-tracker="1036"
                          sx={{
                            width: 106,
                            mt: 0.5,
                            ...CAPTION_SX,
                            color: "secondary.main",
                            cursor: "pointer",
                          }}
                        >
                          <ContentCopyIcon sx={{ fontSize: 12, mr: 0.5, verticalAlign: "middle" }} />
                          {otherIpCount} shared: {shownIds}
                          {remaining > 0 ? ` +${remaining}` : ""}
                        </Box>
                      );
                    })()}
                  </TableCell>
                  <TableCell>
                    <Box
                      onClick={() => openBrandMatch(lead.id)}
                      title="Run the brand match query for this lead"
                      data-tracker="1035"
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 1,
                        px: "5px",
                        py: 0.5,
                        borderRadius: 1,
                        cursor: "pointer",
                        color: "secondary.main",
                        "&:hover": { bgcolor: (theme) => alpha(theme.palette.secondary.main, 0.04) },
                      }}
                    >
                      <ChecklistIcon sx={{ fontSize: 18 }} />
                      <Typography
                        component="span"
                        sx={{
                          fontFamily: "Roboto",
                          fontWeight: 500,
                          fontSize: 13,
                          lineHeight: "22px",
                          letterSpacing: "0.46px",
                          textTransform: "uppercase",
                          textDecoration: "underline",
                        }}
                      >
                        Check
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {pageLeads.length === 0 && (
        <Box sx={{ p: 6, textAlign: "center", color: "grey.500", fontSize: 15 }}>
          No leads match the current filters.
        </Box>
      )}

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr auto 1fr",
          alignItems: "center",
          minHeight: 36,
          px: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography sx={{ ...CAPTION_SX, color: "text.secondary" }}>
              Rows per page:
            </Typography>
            <Select
              value={perPage}
              onChange={(e) => setPerPage(Number(e.target.value))}
              size="small"
              variant="standard"
              disableUnderline
              sx={{
                ...CAPTION_SX,
                color: "text.primary",
                "& .MuiSelect-select": {
                  display: "flex",
                  alignItems: "center",
                  py: 0,
                },
              }}
            >
              {pageSizeOptions.map((option) => (
                <MenuItem key={option} value={option} sx={{ fontSize: 12 }}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </Box>
          <Typography sx={{ ...CAPTION_SX, color: "text.primary" }}>
            {totalFiltered === 0 ? 0 : pageStartIndex + 1}-{Math.min(pageStartIndex + perPage, totalFiltered)} of{" "}
            {totalFiltered.toLocaleString("en-US")}
          </Typography>
        </Box>
        <Pagination
          count={totalPages}
          page={page}
          onChange={(_, value) => goToPage(value)}
          color="secondary"
          size="small"
          showFirstButton
          showLastButton
          sx={{
            justifySelf: "center",
            "& .MuiPaginationItem-root": {
              minWidth: 28,
              height: 28,
              borderRadius: "50%",
              fontSize: 14,
              letterSpacing: "0.17px",
            },
          }}
        />
        <Box />
      </Box>
    </Paper>
  );
}
